import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Divider,
  InputAdornment,
} from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { RiskBudgetNotice } from '../../components/options/RiskBudgetNotice';
import { optionsService, OptionsUserSettings } from '../../services/optionsService';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';

const OptionsSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<OptionsUserSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    optionsService
      .getOptionsSettings()
      .then((data) => {
        setSettings(data);
        setLoadError(null);
      })
      .catch((err) => setLoadError(err?.response?.data?.error || err?.message || 'Failed to load settings'));
  };

  useEffect(load, []);

  const handleChange = (field: keyof OptionsUserSettings, value: any) => {
    if (!settings) return;
    setSettings({ ...settings, [field]: value });
    setSaved(false);
  };

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await optionsService.updateOptionsSettings({
        autoTradingEnabled: settings.autoTradingEnabled,
        liveTradingEnabled: settings.liveTradingEnabled,
        capitalAllocationInr: settings.capitalAllocationInr,
        maxDailyLossInr: settings.maxDailyLossInr,
        maxActivePositions: settings.maxActivePositions,
        maxPositionRiskInr: settings.maxPositionRiskInr,
      });
      setSettings(updated);
      setSaved(true);
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || err?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />
      <RiskBudgetNotice showSettingsLink={false} />

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', maxWidth: 720 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <SettingsIcon sx={{ color: '#0284C7' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
            MY OPTIONS TRADING SETTINGS
          </Typography>
        </Stack>
        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 2.5 }}>
          Market data (spot, chain, structure) is shared for everyone. These settings control only your own account:
          whether the automated engine trades on your behalf, how much capital it uses, and your own risk limits.
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {!loadError && !settings && (
          <Typography variant="body2" sx={{ color: '#64748B' }}>
            Loading your settings…
          </Typography>
        )}

        {settings && (
          <Stack spacing={3}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoTradingEnabled}
                    onChange={(e) => handleChange('autoTradingEnabled', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Enable Automated Trading
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      When on, the shared automated engine evaluates the market and can open hedged positions on your own broker account.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Divider />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.liveTradingEnabled}
                    onChange={(e) => handleChange('liveTradingEnabled', e.target.checked)}
                    disabled={!settings.liveTradingMasterSwitch && !settings.liveTradingEnabled}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: settings.liveTradingEnabled ? '#DC2626' : undefined }}>
                      Enable Live Trading (Real Orders)
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      {settings.liveTradingMasterSwitch
                        ? 'Places real orders on your own Kite account instead of simulated paper fills.'
                        : 'Live trading is not enabled on this deployment yet — this account will trade in paper mode regardless of this toggle.'}
                    </Typography>
                  </Box>
                }
              />
              {settings.liveTradingEnabled && settings.liveTradingMasterSwitch && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Live trading is ON. The automated engine will place real orders with real money on your Kite account.
                </Alert>
              )}
            </Box>

            <Divider />

            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Capital Allocation"
                  type="number"
                  value={settings.capitalAllocationInr}
                  onChange={(e) => handleChange('capitalAllocationInr', Number(e.target.value))}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  helperText="Rupee capital this account uses. Position size (lots) is scaled to fit within it."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Max Daily Loss"
                  type="number"
                  value={settings.maxDailyLossInr}
                  onChange={(e) => handleChange('maxDailyLossInr', Number(e.target.value))}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  helperText="New entries stop for the day once this loss is reached."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Max Active Positions"
                  type="number"
                  value={settings.maxActivePositions}
                  onChange={(e) => handleChange('maxActivePositions', Number(e.target.value))}
                  helperText="Maximum concurrent hedged positions on your account (1-20)."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Max Position Risk"
                  type="number"
                  value={settings.maxPositionRiskInr}
                  onChange={(e) => handleChange('maxPositionRiskInr', Number(e.target.value))}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  helperText="A single position's defined max loss may not exceed roughly 2x this value."
                />
              </Grid>
            </Grid>

            {saveError && <Alert severity="error">{saveError}</Alert>}
            {saved && <Alert severity="success">Settings saved.</Alert>}

            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving} sx={{ bgcolor: '#0284C7' }}>
                {saving ? 'Saving…' : 'Save Settings'}
              </Button>
              <Button variant="outlined" onClick={load} disabled={saving}>
                Reload
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default OptionsSettingsPage;
